import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { assistentes as api, type ConferenciaDoToken, type SituacaoDoToken } from '../api/assistentes'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'

/**
 * O token com que este assistente assina o que escreve na plataforma
 * (demanda 052).
 *
 * 🔴 **O campo nasce vazio mesmo quando há token gravado**, e não é esquecimento:
 * a API nunca devolve o valor (`SEC-06`). O que a tela mostra são os quatro
 * últimos caracteres — o bastante para conferir se é o que foi colado. Por isso
 * salvar em branco **não apaga**: remover é o botão próprio.
 *
 * O botão de conferir existe por causa do modo como errar aqui falha. Sem ele,
 * um token trocado só aparece na primeira mensagem que o cliente não recebeu,
 * cinco tentativas depois, num campo de erro que ninguém lê.
 */
export function IdentidadeNaPlataforma({ marca, id }: { marca: string; id: number }) {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const editavel = pode('ParametroGerenciar')

  const [situacao, setSituacao] = useState<SituacaoDoToken | null>(null)
  const [token, setToken] = useState('')
  const [conferencia, setConferencia] = useState<ConferenciaDoToken | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const carregar = useCallback(() => {
    api.situacaoDoToken(marca, id).then(setSituacao).catch(() => setSituacao(null))
  }, [marca, id])

  useEffect(carregar, [carregar])

  async function executar(acao: () => Promise<unknown>) {
    setErro(null)
    setOcupado(true)

    try {
      await acao()
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setOcupado(false)
    }
  }

  const gravar = () =>
    executar(async () => {
      setSituacao(await api.gravarToken(marca, id, token))
      setToken('')
      setConferencia(null)
    })

  const apagar = () =>
    executar(async () => {
      setSituacao(await api.apagarToken(marca, id))
      setConferencia(null)
    })

  // Sem texto digitado, confere o que está gravado — que é a pergunta de quem
  // abre a tela para descobrir se o cadastro ainda vale.
  const conferir = () =>
    executar(async () => setConferencia(await api.conferirToken(marca, id, token || undefined)))

  return (
    <Cartao titulo={t('assistentes.token')}>
      <p className="usuarios__descricao">{t('assistentes.tokenAjuda')}</p>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {situacao && (
        <Aviso tom={situacao.proprio ? 'sucesso' : 'info'}>
          {situacao.proprio
            ? t('assistentes.tokenGravado', { fim: situacao.ultimosQuatro })
            : situacao.origem === 'administrativo'
              ? t('assistentes.tokenAdministrativo')
              : t('assistentes.tokenAusente')}
        </Aviso>
      )}

      {editavel && (
        <>
          <Campo
            rotulo={t('assistentes.token')}
            type="password"
            value={token}
            autoComplete="off"
            onChange={(e) => setToken(e.target.value)}
          />

          <div className="usuarios__acoes">
            {/* 🔴 Rótulo próprio, e não "Salvar": a tela já tem o Salvar do
                comportamento do assistente, e dois botões com o mesmo nome na
                mesma página são ambíguos para quem navega por leitor de tela —
                e foram ambíguos para o teste, que é o mesmo problema. */}
            <Botao onClick={gravar} disabled={ocupado || token.trim() === ''}>
              {t('assistentes.tokenGravar')}
            </Botao>

            <Botao variante="discreto" onClick={conferir} disabled={ocupado}>
              {t('assistentes.tokenConferir')}
            </Botao>

            {situacao?.proprio && (
              <Botao variante="discreto" onClick={apagar} disabled={ocupado}>
                {t('assistentes.tokenApagar')}
              </Botao>
            )}
          </div>
        </>
      )}

      {conferencia && (
        <Aviso tom={conferencia.conferido ? 'sucesso' : 'perigo'}>
          {conferencia.conferido
            ? t('assistentes.tokenDono', { dono: conferencia.dono })
            : conferencia.erro}
        </Aviso>
      )}
    </Cartao>
  )
}
